import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandPointer, faTimes, faSave, faUser, faKey, faExclamationTriangle,
  faCheckCircle, faSync
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from '../../config/axiosConfig';

const ManualCredentialMapper = ({ profileId, isDarkMode, onClose, onMappingComplete }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [selectedPassword, setSelectedPassword] = useState(null);

  useEffect(() => {
    loadFields();
  }, [profileId]);

  const loadFields = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/auth-profiles/${profileId}/workflow/fields`);
      setFields(response.data.fields || []);

      // Auto-select if only one username/password-like field found
      const usernamePatterns = /^(username|user|email|login|account|log|user_login)$/i;
      const passwordPatterns = /^(password|pass|pwd|secret|user_pass)$/i;

      const usernameField = response.data.fields.find(f => usernamePatterns.test(f.name));
      const passwordField = response.data.fields.find(f => passwordPatterns.test(f.name));

      if (usernameField && !selectedUsername) setSelectedUsername(usernameField.name);
      if (passwordField && !selectedPassword) setSelectedPassword(passwordField.name);

    } catch (error) {
      console.error('Failed to load fields:', error);
      toast.error('Failed to load fields');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedUsername || !selectedPassword) {
      toast.error('Please select both username and password fields');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`/auth-profiles/${profileId}/workflow/map-credentials`, {
        usernameField: selectedUsername,
        passwordField: selectedPassword
      });

      toast.success('✓ Credentials mapped successfully!');
      if (onMappingComplete) {
        onMappingComplete();
      }
      onClose();
    } catch (error) {
      console.error('Failed to map credentials:', error);
      toast.error('Failed to map credentials');
    } finally {
      setSaving(false);
    }
  };

  const containerClass = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
  const borderClass = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const hoverClass = isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';
  const inputClass = isDarkMode
    ? 'bg-gray-700 border-gray-600 text-gray-100'
    : 'bg-white border-gray-300 text-gray-900';

  if (loading) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${isDarkMode ? 'bg-black bg-opacity-75' : 'bg-gray-900 bg-opacity-50'}`}>
        <div className={`${containerClass} p-6 rounded-lg shadow-xl`}>
          <FontAwesomeIcon icon={faSync} spin className="mr-2" />
          Loading fields...
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDarkMode ? 'bg-black bg-opacity-75' : 'bg-gray-900 bg-opacity-50'}`}>
      <div className={`${containerClass} rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${borderClass}`}>
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faHandPointer} className="text-blue-500" />
            <div>
              <h2 className="text-xl font-semibold">Manual Credential Mapping</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Auto-detection failed. Please select the correct fields manually.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded ${hoverClass}`}
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Info Banner */}
        <div className={`m-4 p-3 rounded-lg ${isDarkMode ? 'bg-yellow-900 bg-opacity-20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-start gap-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold mb-1">Select the fields that contain the username and password</p>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                These fields will be used for automated login replay and credential substitution.
              </p>
            </div>
          </div>
        </div>

        {/* Fields Table */}
        <div className="flex-1 overflow-y-auto p-4">
          {fields.length === 0 ? (
            <div className="text-center py-8">
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                No fields found in the login request.
              </p>
            </div>
          ) : (
            <div className={`border rounded-lg overflow-hidden ${borderClass}`}>
              <table className="w-full text-sm">
                <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-4 py-3 text-left font-semibold border-b ${borderClass}`}>Field Name</th>
                    <th className={`px-4 py-3 text-left font-semibold border-b ${borderClass}`}>Value</th>
                    <th className={`px-4 py-3 text-center font-semibold border-b ${borderClass}`}>
                      <FontAwesomeIcon icon={faUser} className="mr-1" />
                      Username
                    </th>
                    <th className={`px-4 py-3 text-center font-semibold border-b ${borderClass}`}>
                      <FontAwesomeIcon icon={faKey} className="mr-1" />
                      Password
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => (
                    <tr
                      key={index}
                      className={`${index % 2 === 0 ? (isDarkMode ? 'bg-gray-800' : 'bg-white') : (isDarkMode ? 'bg-gray-750' : 'bg-gray-50')} ${hoverClass}`}
                    >
                      <td className={`px-4 py-3 border-b ${borderClass}`}>
                        <code className="font-mono text-blue-400">{field.name}</code>
                      </td>
                      <td className={`px-4 py-3 border-b ${borderClass}`}>
                        {field.isSensitive ? (
                          <span className="text-yellow-400 font-mono">***</span>
                        ) : (
                          <span className="font-mono text-green-400">
                            {field.value.length > 50 ? field.value.substring(0, 50) + '...' : field.value}
                          </span>
                        )}
                      </td>
                      <td className={`px-4 py-3 border-b ${borderClass} text-center`}>
                        <input
                          type="radio"
                          name="username"
                          checked={selectedUsername === field.name}
                          onChange={() => setSelectedUsername(field.name)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                      <td className={`px-4 py-3 border-b ${borderClass} text-center`}>
                        <input
                          type="radio"
                          name="password"
                          checked={selectedPassword === field.name}
                          onChange={() => setSelectedPassword(field.name)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Selected Summary */}
          {(selectedUsername || selectedPassword) && (
            <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-green-900 bg-opacity-20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                <span className="font-semibold">Current Selection:</span>
              </div>
              <div className="space-y-1 text-sm">
                <div>
                  <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-400" />
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Username field:</span>
                  {selectedUsername ? (
                    <code className="ml-2 px-2 py-0.5 rounded bg-black bg-opacity-20">{selectedUsername}</code>
                  ) : (
                    <span className="ml-2 text-yellow-500">Not selected</span>
                  )}
                </div>
                <div>
                  <FontAwesomeIcon icon={faKey} className="mr-2 text-yellow-400" />
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Password field:</span>
                  {selectedPassword ? (
                    <code className="ml-2 px-2 py-0.5 rounded bg-black bg-opacity-20">{selectedPassword}</code>
                  ) : (
                    <span className="ml-2 text-yellow-500">Not selected</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-4 border-t ${borderClass}`}>
          <div className="text-sm text-gray-500">
            {fields.length} fields found in login POST request
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-500 hover:bg-gray-600'} text-white`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedUsername || !selectedPassword || saving}
              className={`px-4 py-2 rounded ${
                selectedUsername && selectedPassword && !saving
                  ? isDarkMode
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-green-500 hover:bg-green-600'
                  : 'bg-gray-400 cursor-not-allowed'
              } text-white`}
            >
              {saving ? (
                <>
                  <FontAwesomeIcon icon={faSync} spin className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Save Mapping
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualCredentialMapper;
